import { useState, type FormEvent } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { BotonEnlace, Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, Seccion } from '@/components/Card';
import { Fila, Filas } from '@/components/Fila';
import { DateField, NumberField, TextField } from '@/components/Field';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import {
  guardarDecisionDePropuesta,
  guardarMantenimientoManual,
  leerEstimadoDeMantenimiento,
  leerMantenimientos,
  leerPropuestaPorFormula,
  type EstimadoDeHoy,
} from '@/db/mantenimiento';
import { guardarMetasManuales, leerMetasDelDia } from '@/db/metas';
import {
  DESCRIPCION_ACTIVIDAD,
  NIVELES_ACTIVIDAD,
  type MacrosDiarios,
  type Maintenance,
  type NivelActividad,
  type PeriodoMetasManuales,
  type Settings,
  type Sexo,
} from '@/db/types';
import { formatearCorto, formatearCortoConAnio, type FechaLocal } from '@/domain/dates';
import { mantenimientoVigente } from '@/domain/etapas';
import {
  DIFERENCIA_PARA_REOFRECER_KCAL,
  DONDE_CAPTURAR,
  debeOfrecerse,
  kcalDeMacros,
  macrosCuadran,
  textoDeFaltantes,
  textoFaltaParaEstimar,
  type DatoDeFormula,
  type DiaDeEstimacion,
  type MacrosPropuestos,
  type ResultadoFormula,
} from '@/domain/mantenimiento';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import { formatearGramos, formatearKcal } from '@/domain/nutricion';
import { validarFechaNacimiento } from '@/domain/perfil';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Opciones, rutaDelDia } from '@/features/food/comunes';

// ---------------------------------------------------------------------------
// Mantenimiento manual (Req. 22.2, 22.3)
// ---------------------------------------------------------------------------

export function MantenimientoPage() {
  const hoy = useFechaHoy();
  const registros = useLiveQuery(() => leerMantenimientos(), []);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const propuesta = useLiveQuery(() => leerPropuestaPorFormula(hoy), [hoy]);
  const estimado = useLiveQuery(() => leerEstimadoDeMantenimiento(hoy), [hoy]);
  if (registros === undefined || ajustes === undefined || propuesta === undefined || estimado === undefined) return null;
  const vigente = mantenimientoVigente(registros, hoy);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Tu mantenimiento."
        descripcion="Las calorías con las que tu peso se mantiene. Tu etapa de mantenimiento usa este valor."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      <Card>
        <span className="antetitulo">Vigente hoy</span>
        {vigente === null || vigente.kcal === null ? (
          <>
            <p className="mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">Sin mantenimiento capturado.</p>
            <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
              Dato insuficiente: la app no supone una cifra. Mientras no lo captures, las metas que
              dependen de él dicen "falta el mantenimiento".
            </p>
          </>
        ) : (
          <>
            <p className="mt-3 text-[34px] leading-none tracking-[-1.2px] tabular-nums text-texto">
              {formatearKcal(vigente.kcal)}
            </p>
            <p className="mt-2 text-[13px] text-texto-suave">Desde el {formatearCortoConAnio(vigente.desde)}.</p>
            {/* Los macros del mantenimiento, si se aceptaron (0.5.1) */}
            <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
              {vigente.macros == null
                ? 'Sin macros: tu etapa de mantenimiento usará los que tenga ella, si tiene.'
                : `Con ${textoMacros(vigente.macros)}. Los usan los días de tu etapa de mantenimiento que no tengan sus propios macros.`}
            </p>
          </>
        )}
      </Card>

      <PropuestaDeFormula
        hoy={hoy}
        ajustes={ajustes}
        resultado={propuesta.resultado}
        registros={registros}
        vigenteKcal={vigente?.kcal ?? null}
      />

      <PropuestaEstimada hoy={hoy} estimado={estimado} registros={registros} vigenteKcal={vigente?.kcal ?? null} />

      <FormularioMantenimiento hoy={hoy} />

      {registros.length > 0 && (
        <Seccion
          titulo="Historial"
          descripcion="No se borra. Para corregir, captura el valor correcto desde hoy: los días anteriores conservan el que tenían."
        >
          <ul className="divide-y divide-borde">
            {registros.map((r) => (
              <li key={r.id} className="py-3">
                <p className="flex justify-between gap-3 text-[14px] text-texto">
                  <span className="font-medium tabular-nums">{r.kcal === null ? 'Rechazado' : formatearKcal(r.kcal)}</span>
                  <span className="text-[12px] text-texto-suave">Desde el {formatearCortoConAnio(r.desde)}</span>
                </p>
                {r.macros != null && <p className="text-[12px] tabular-nums text-texto-suave">{textoMacros(r.macros)}</p>}
                <p className="text-[12px] text-texto-suave">{r.detalle}</p>
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La tarjeta de una propuesta (Req. 22.5, 22.6, 22.10)
// ---------------------------------------------------------------------------

/** Lo que se muestra de una propuesta, venga de la fórmula o del estimado. */
interface PropuestaVisible {
  kcal: number;
  macros: MacrosPropuestos | null;
  /** Cada renglón del cálculo, con sus números. */
  pasos: readonly string[];
  /** Lo que da por cierto. */
  supuestos: readonly string[];
  /** La línea que queda en el historial. */
  detalle: string;
}

/**
 * La app propone; la decisión es de la persona. El número, los macros, de
 * dónde sale paso a paso, sus supuestos y las tres decisiones. Es la misma
 * tarjeta para la fórmula y para el estimado: así las dos propuestas se leen
 * y se deciden igual.
 */
function TarjetaDePropuesta({
  hoy,
  origen,
  antetitulo,
  propuesta,
  registros,
  vigenteKcal,
}: {
  hoy: FechaLocal;
  origen: 'formula' | 'estimado';
  antetitulo: string;
  propuesta: PropuestaVisible;
  registros: readonly Maintenance[];
  vigenteKcal: number | null;
}) {
  const [id, setId] = useState(() => nuevoId('man'));
  const [modificando, setModificando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState<string | null>(null);

  const ofrecimiento = debeOfrecerse(propuesta.kcal, vigenteKcal, registros, origen);
  const macros = propuesta.macros;

  async function decidir(
    decision: 'aceptada' | 'modificada' | 'rechazada',
    kcal: number | null,
    macrosElegidos: MacrosDiarios | null,
  ) {
    if (guardando) return;
    setError(null);
    setListo(null);
    setGuardando(true);
    try {
      const r = await guardarDecisionDePropuesta(
        {
          id,
          origen,
          propuestaKcal: propuesta.kcal,
          kcal,
          macros: macrosElegidos,
          decision,
          supuestos: [...propuesta.supuestos],
          detalle:
            decision === 'rechazada'
              ? `Propuesta de ${formatearKcal(propuesta.kcal)} rechazada. ${propuesta.detalle}`
              : decision === 'modificada'
                ? `Propuesta de ${formatearKcal(propuesta.kcal)}, guardada como ${formatearKcal(kcal ?? 0)}${
                    macrosElegidos === null ? '' : ` con ${textoMacros(macrosElegidos)}`
                  }. ${propuesta.detalle}`
                : propuesta.detalle,
        },
        hoy,
      );
      setId(nuevoId('man'));
      setModificando(false);
      setListo(
        r.kcal === null
          ? 'Rechazada. Tu mantenimiento no cambió y no se te vuelve a ofrecer hasta que cambie 50 kcal o más.'
          : `Guardado: ${formatearKcal(r.kcal)}${r.macros == null ? '' : ` con ${textoMacros(r.macros)}`} desde hoy. Los días anteriores conservan lo que tenían.`,
      );
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <span className="antetitulo">{antetitulo}</span>
        <p className="mt-2 text-[34px] leading-none tracking-[-1.2px] tabular-nums text-texto">{formatearKcal(propuesta.kcal)}</p>
        <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
          {ofrecimiento.ofrecer === true
            ? vigenteKcal === null
              ? 'Todavía no tienes un mantenimiento guardado: puedes usar este o capturar otro abajo.'
              : `Tu mantenimiento vigente es ${formatearKcal(vigenteKcal)}.`
            : ofrecimiento.razon === 'ya_es_el_vigente'
              ? 'Es el mismo que ya tienes vigente: no hay nada que cambiar.'
              : ofrecimiento.razon === 'cerca_del_vigente'
                ? `Queda a menos de ${DIFERENCIA_PARA_REOFRECER_KCAL} kcal de tu mantenimiento vigente (${formatearKcal(
                    vigenteKcal ?? 0,
                  )}): por tan poco no se te pide decidir.`
                : `Rechazaste ${formatearKcal(ofrecimiento.rechazadaKcal ?? 0)} y este resultado no cambia 50 kcal o más, así que no se te vuelve a preguntar.`}
        </p>
        {macros !== null && (
          <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-borde pt-4">
            {(
              [
                ['Proteína', macros.proteinaG, macros.porcentajes.proteina],
                ['Carbohidratos', macros.carbohidratosG, macros.porcentajes.carbohidratos],
                ['Grasa', macros.grasaG, macros.porcentajes.grasa],
              ] as const
            ).map(([nombre, gramos, pct]) => (
              <div key={nombre} className="min-w-0">
                <dt className="text-[12px] text-texto-suave">{nombre}</dt>
                <dd className="mt-1">
                  <span className="block text-[17px] tabular-nums text-texto">{formatearGramos(gramos)}</span>
                  <span className="block text-[12px] tabular-nums text-texto-suave">{pct} %</span>
                </dd>
              </div>
            ))}
          </dl>
        )}
        {macros !== null && (
          <p className="mt-2 text-[12px] leading-relaxed text-texto-suave">
            Los macros cuadran con las calorías: suman {formatearKcal(macros.kcalDeLosMacros)}
            {macros.diferenciaKcal === 0
              ? ', justo la propuesta'
              : `, ${macros.diferenciaKcal > 0 ? '+' : '−'}${Math.abs(macros.diferenciaKcal)} kcal por redondear a 5 g`}
            . Proteína de {formatearNumero(macros.proteinaPorKg, { decimales: 1, fijos: false })} g por kg de tu peso.
          </p>
        )}
        <details className="mt-3 border-t border-borde pt-2">
          <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-marca">
            De dónde sale este número
          </summary>
          <ol className="space-y-1.5 pb-1 text-[12px] leading-relaxed text-texto-suave">
            {[...propuesta.pasos, ...(macros?.pasos ?? [])].map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
          <p className="mt-2 text-[12px] font-medium text-texto">Lo que da por cierto</p>
          <ul className="list-disc space-y-1 pl-5 pt-1 text-[12px] leading-relaxed text-texto-suave">
            {propuesta.supuestos.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </details>
      </Card>

      {ofrecimiento.ofrecer === true && (
        <div className="grid gap-2">
          <Button icono={Check} disabled={guardando} onClick={() => void decidir('aceptada', propuesta.kcal, macros)}>
            {guardando
              ? 'Guardando…'
              : macros === null
                ? `Usar ${formatearKcal(propuesta.kcal)} desde hoy`
                : `Usar estas kcal y macros desde hoy`}
          </Button>
          {modificando ? (
            <EditarPropuesta
              propuesta={propuesta}
              guardando={guardando}
              onGuardar={(kcal, elegidos) => void decidir('modificada', kcal, elegidos)}
              onCancelar={() => setModificando(false)}
            />
          ) : (
            <Button variante="secundario" onClick={() => setModificando(true)}>
              {macros === null ? 'Modificar el valor' : 'Modificar los valores'}
            </Button>
          )}
          <Button variante="fantasma" disabled={guardando} onClick={() => void decidir('rechazada', null, null)}>
            No usarla
          </Button>
        </div>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}
      {listo !== null && <Banner tono="exito" titulo={listo} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Propuesta por fórmula (Req. 22.1, 22.5 a 22.8, 22.10)
// ---------------------------------------------------------------------------

/**
 * La propuesta de Mifflin-St Jeor con los datos de la persona. Si falta un
 * dato, dice cuál y lo pide aquí mismo, sin suponerlo.
 */
function PropuestaDeFormula({
  hoy,
  ajustes,
  resultado,
  registros,
  vigenteKcal,
}: {
  hoy: FechaLocal;
  ajustes: Settings;
  resultado: ResultadoFormula;
  registros: readonly Maintenance[];
  vigenteKcal: number | null;
}) {
  return (
    <Seccion
      titulo="Propuesta de la app"
      descripcion="Con la fórmula de Mifflin-St Jeor y tus datos. Es una propuesta: nada cambia hasta que tú decidas."
    >
      {resultado.tipo === 'faltan' ? (
        <DatosParaLaFormula faltan={resultado.faltan} ajustes={ajustes} hoy={hoy} />
      ) : (
        <div className="space-y-4">
          <TarjetaDePropuesta
            hoy={hoy}
            origen="formula"
            antetitulo="Por fórmula, hoy"
            propuesta={resultado.propuesta}
            registros={registros}
            vigenteKcal={vigenteKcal}
          />
          <DatosParaLaFormula faltan={[]} ajustes={ajustes} hoy={hoy} />
        </div>
      )}
    </Seccion>
  );
}

// ---------------------------------------------------------------------------
// Propuesta estimada con los días registrados (Req. 22.4, 22.9; 0.7.0)
// ---------------------------------------------------------------------------

/**
 * El estimado sale de lo registrado, así que la pantalla enseña los días que
 * entraron y lleva a cada uno para corregirlo (Req. 22.9). Si faltan días o
 * tendencia, dice qué falta en lugar de estimar con menos (Req. 22.7).
 */
function PropuestaEstimada({
  hoy,
  estimado,
  registros,
  vigenteKcal,
}: {
  hoy: FechaLocal;
  estimado: EstimadoDeHoy;
  registros: readonly Maintenance[];
  vigenteKcal: number | null;
}) {
  const { resultado, dias } = estimado;
  return (
    <Seccion
      titulo="Propuesta con tus días registrados"
      descripcion={`Con las kcal que registraste del ${formatearCorto(estimado.desde)} al ${formatearCorto(
        estimado.hasta,
      )} y el cambio de tu tendencia de peso. Hoy no entra: todavía no termina.`}
    >
      <div className="space-y-4">
        {resultado.tipo === 'faltan' ? (
          <Banner tono="info" titulo="Todavía no se puede estimar">
            {textoFaltaParaEstimar(resultado, dias.length)}
          </Banner>
        ) : (
          <TarjetaDePropuesta
            hoy={hoy}
            origen="estimado"
            antetitulo="Con lo que registraste"
            propuesta={resultado.estimado}
            registros={registros}
            vigenteKcal={vigenteKcal}
          />
        )}
        <DiasDelEstimado dias={dias} hoy={hoy} />
      </div>
    </Seccion>
  );
}

/** Los días de la ventana, del más reciente al más antiguo, cada uno con su enlace (Req. 22.9). */
function DiasDelEstimado({ dias, hoy }: { dias: readonly DiaDeEstimacion[]; hoy: FechaLocal }) {
  const cuentan = dias.filter((d) => d.conRegistros && !d.incompleto).length;
  const detalleDe = (d: DiaDeEstimacion): string => {
    if (!d.conRegistros) return 'Sin registros: no cuenta';
    if (d.incompleto) return `${formatearKcal(d.kcal)} · marcado como incompleto: no cuenta`;
    return `${formatearKcal(d.kcal)} · cuenta`;
  };
  return (
    <details className="rounded-panel bg-superficie p-4">
      <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-marca">
        Los días que usé ({cuentan} de {dias.length})
      </summary>
      <p className="pt-1 text-[12px] leading-relaxed text-texto-suave">
        Toca un día para revisarlo en Comida. Al corregirlo, el estimado se recalcula solo; lo que
        ya decidiste no cambia.
      </p>
      <Filas>
        {[...dias].reverse().map((d) => (
          <Fila key={d.fecha} titulo={formatearCortoConAnio(d.fecha)} detalle={detalleDe(d)} a={rutaDelDia(d.fecha, hoy)} />
        ))}
      </Filas>
    </details>
  );
}

/** "140 g de proteína, 135 g de carbohidratos y 45 g de grasa" */
function textoMacros(m: MacrosDiarios): string {
  return `${formatearGramos(m.proteinaG)} de proteína, ${formatearGramos(m.carbohidratosG)} de carbohidratos y ${formatearGramos(
    m.grasaG,
  )} de grasa`;
}

/**
 * Revisar y cambiar la propuesta antes de aceptarla: las kcal y los tres
 * macros juntos. Mientras se escribe se dice cuánto suman, para que no quede
 * una meta que no cuadra con sus calorías.
 */
function EditarPropuesta({
  propuesta,
  guardando,
  onGuardar,
  onCancelar,
}: {
  propuesta: PropuestaVisible;
  guardando: boolean;
  onGuardar: (kcal: number, macros: MacrosDiarios | null) => void;
  onCancelar: () => void;
}) {
  const texto = (v: number | null) => (v === null ? '' : String(v));
  const [kcal, setKcal] = useState(texto(propuesta.kcal));
  const [proteina, setProteina] = useState(texto(propuesta.macros?.proteinaG ?? null));
  const [carbohidratos, setCarbohidratos] = useState(texto(propuesta.macros?.carbohidratosG ?? null));
  const [grasa, setGrasa] = useState(texto(propuesta.macros?.grasaG ?? null));
  const [error, setError] = useState<string | null>(null);

  const numero = (t: string) => leerDecimal(t);
  const kcalNum = numero(kcal);
  const gramos = [numero(proteina), numero(carbohidratos), numero(grasa)];
  const conMacros = gramos.every((g) => g !== null);
  const macros = conMacros
    ? { proteinaG: gramos[0] as number, carbohidratosG: gramos[1] as number, grasaG: gramos[2] as number }
    : null;
  const suman = macros === null ? null : kcalDeMacros(macros);
  const cuadran = macros === null || kcalNum === null ? null : macrosCuadran(macros, kcalNum);

  const campo = (etiqueta: string, valor: string, fijar: (v: string) => void) => (
    <NumberField
      etiqueta={etiqueta}
      valor={valor}
      onCambio={(v) => {
        fijar(v);
        setError(null);
      }}
      decimal={false}
    />
  );

  return (
    <div className="space-y-4 rounded-panel bg-superficie p-4">
      {campo('kcal al día de la propuesta', kcal, setKcal)}
      <div className="grid grid-cols-3 gap-2">
        {campo('Proteína (g)', proteina, setProteina)}
        {campo('Carbohidratos (g)', carbohidratos, setCarbohidratos)}
        {campo('Grasa (g)', grasa, setGrasa)}
      </div>
      <p className="text-[12px] leading-relaxed text-texto-suave" aria-live="polite">
        {suman === null
          ? 'Deja los tres macros vacíos para guardar solo las kcal, o escribe los tres.'
          : cuadran === true
            ? `Cuadran: esos macros suman ${formatearKcal(suman)}.`
            : `Esos macros suman ${formatearKcal(suman)} y la meta dice ${kcalNum === null ? '—' : formatearKcal(kcalNum)}: ajústalos para que cuadren.`}
      </p>
      {error !== null && (
        <Banner tono="alerta" titulo="Revisa los valores">
          {error}
        </Banner>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button
          disabled={guardando || cuadran === false}
          onClick={() => {
            if (kcalNum === null) return setError('Escribe las kcal que quieres usar.');
            if (!conMacros && gramos.some((g) => g !== null)) {
              return setError('Escribe los tres macros o deja los tres vacíos.');
            }
            onGuardar(Math.round(kcalNum), macros);
          }}
        >
          Guardar estos valores
        </Button>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

const SEXOS: readonly { valor: Sexo; etiqueta: string }[] = [
  { valor: 'mujer', etiqueta: 'Mujer' },
  { valor: 'hombre', etiqueta: 'Hombre' },
];

/**
 * Los datos de la fórmula, pedidos donde se usan (design.md, 7.3). El peso y
 * la estatura viven en Cuerpo; el resto se captura aquí. Lo que ya estaba
 * guardado no se borra.
 */
function DatosParaLaFormula({ faltan, ajustes, hoy }: { faltan: readonly DatoDeFormula[]; ajustes: Settings; hoy: FechaLocal }) {
  const incompleto = faltan.length > 0;
  const [sexo, setSexo] = useState<Sexo | ''>(ajustes.sexo ?? '');
  const [nacimiento, setNacimiento] = useState(ajustes.fechaNacimiento ?? '');
  const [actividad, setActividad] = useState<NivelActividad | ''>(ajustes.nivelActividad ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    if (guardando) return;
    const fecha = validarFechaNacimiento(nacimiento, hoy);
    if (!fecha.valido) return setError(fecha.error);
    setError(null);
    setGuardando(true);
    try {
      await actualizarSettings({
        sexo: sexo === '' ? null : sexo,
        fechaNacimiento: fecha.valor,
        nivelActividad: actividad === '' ? null : actividad,
      });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  const campos = (
    <div className="space-y-4">
      <Opciones etiqueta="Sexo" valor={sexo} opciones={[{ valor: '', etiqueta: 'Sin dato' }, ...SEXOS]} onCambio={setSexo} />
      <DateField etiqueta="Fecha de nacimiento" valor={nacimiento} onCambio={setNacimiento} max={hoy} />
      <Opciones
        etiqueta="Nivel de actividad"
        valor={actividad}
        opciones={[
          { valor: '', etiqueta: 'Sin dato' },
          ...NIVELES_ACTIVIDAD.map((n) => ({
            valor: n,
            etiqueta: `${DESCRIPCION_ACTIVIDAD[n].nombre} · ${DESCRIPCION_ACTIVIDAD[n].descripcion.toLocaleLowerCase('es-MX')}`,
          })),
        ]}
        onCambio={setActividad}
      />
      <p className="text-[12px] leading-relaxed text-texto-suave">
        El nivel agrupa todo tu movimiento del día, incluido lo que entrenas; no lo mide. Si dudas
        entre dos, elige el de menos: así la propuesta no queda alta.
      </p>
      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}
      <Button variante={incompleto ? 'primario' : 'secundario'} disabled={guardando} onClick={() => void guardar()}>
        {guardando ? 'Guardando…' : 'Guardar mis datos'}
      </Button>
    </div>
  );

  if (!incompleto) {
    return (
      <details className="rounded-panel bg-superficie p-4">
        <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-marca">
          Revisar los datos que usé
        </summary>
        <div className="pt-2">{campos}</div>
      </details>
    );
  }

  return (
    <div className="space-y-4">
      <Banner tono="info" titulo={textoDeFaltantes(faltan)}>
        La app no supone ninguno de estos datos. Se guardan en tu teléfono y viajan en tus
        respaldos, como todo lo demás.
      </Banner>
      {faltan.includes('peso') && (
        <BotonEnlace to={DONDE_CAPTURAR.peso} variante="secundario">
          Registrar mi peso
        </BotonEnlace>
      )}
      {faltan.includes('estatura') && (
        <BotonEnlace to={DONDE_CAPTURAR.estatura} variante="secundario">
          Agregar mi estatura
        </BotonEnlace>
      )}
      {faltan.some((d) => d === 'sexo' || d === 'nacimiento' || d === 'actividad') && campos}
    </div>
  );
}

function FormularioMantenimiento({ hoy }: { hoy: FechaLocal }) {
  const [id, setId] = useState(() => nuevoId('man'));
  const [kcal, setKcal] = useState('');
  const [desde, setDesde] = useState<string>(hoy);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState<string | null>(null);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    const valor = leerDecimal(kcal);
    if (valor === null) return setError('Escribe las kcal de tu mantenimiento.');
    setError(null);
    setListo(null);
    setGuardando(true);
    try {
      const r = await guardarMantenimientoManual(
        { id, desde, kcal: Math.round(valor), nota: nota.trim() === '' ? null : nota },
        hoy,
      );
      setListo(`Guardado: ${formatearKcal(r.kcal ?? 0)} desde el ${formatearCortoConAnio(r.desde)}.`);
      setId(nuevoId('man'));
      setKcal('');
      setNota('');
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Seccion titulo="Capturar mantenimiento" descripcion="Por ejemplo, el que te indique tu nutrióloga.">
      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <NumberField etiqueta="kcal al día" valor={kcal} onCambio={setKcal} decimal={false} />
        <DateField
          etiqueta="Desde"
          valor={desde}
          onCambio={setDesde}
          min={hoy}
          ayuda="Hoy o una fecha futura. Los días anteriores conservan el mantenimiento que tenían."
        />
        <TextField etiqueta="Nota (opcional)" valor={nota} onCambio={setNota} maxLength={80} placeholder="Indicado por mi nutrióloga" />
        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}
        {listo !== null && <Banner tono="exito" titulo={listo} />}
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar mantenimiento'}
        </Button>
      </form>
    </Seccion>
  );
}

// ---------------------------------------------------------------------------
// Metas sin plan (Req. 18.10, 21.7): las del teléfono de Fernando
// ---------------------------------------------------------------------------

export function MetasManualesPage() {
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const deHoy = useLiveQuery(() => leerMetasDelDia(hoy), [hoy]);
  if (ajustes === undefined || deHoy === undefined) return null;
  return (
    <FormularioMetasManuales
      key={ajustes.perfilId}
      ajustes={ajustes}
      hayPlanHoy={deHoy.metas.origen === 'etapa'}
      hoy={hoy}
    />
  );
}

function textoPeriodo(p: PeriodoMetasManuales): string {
  const partes = [
    p.kcal === null ? null : formatearKcal(p.kcal),
    p.proteinaG === null ? null : `P ${formatearGramos(p.proteinaG)}`,
    p.carbohidratosG === null ? null : `C ${formatearGramos(p.carbohidratosG)}`,
    p.grasaG === null ? null : `G ${formatearGramos(p.grasaG)}`,
    p.ayunoHoras === null ? null : `ayuno ${p.ayunoHoras} h`,
  ].filter(Boolean);
  return partes.length === 0 ? 'Sin metas' : partes.join(' · ');
}

function FormularioMetasManuales({
  ajustes,
  hayPlanHoy,
  hoy,
}: {
  ajustes: Settings;
  hayPlanHoy: boolean;
  hoy: FechaLocal;
}) {
  const texto = (v: number | null) => (v === null ? '' : String(v));
  const [kcal, setKcal] = useState(texto(ajustes.metaKcalManual));
  const [proteina, setProteina] = useState(texto(ajustes.metaProteinaManualG));
  const [carbohidratos, setCarbohidratos] = useState(texto(ajustes.metaCarbohidratosManualG));
  const [grasa, setGrasa] = useState(texto(ajustes.metaGrasaManualG));
  const [ayuno, setAyuno] = useState(texto(ajustes.metaAyunoManualHoras));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    const campos = [kcal, proteina, carbohidratos, grasa, ayuno];
    if (campos.some((t) => t.trim() !== '' && leerDecimal(t) === null)) return setError('Revisa las metas: deben ser números.');
    const valor = (t: string) => (t.trim() === '' ? null : leerDecimal(t));
    const metaKcal = valor(kcal);
    if (metaKcal !== null && (metaKcal < 500 || metaKcal > 10_000)) return setError('La meta de kcal debe estar entre 500 y 10,000.');
    setError(null);
    setListo(false);
    setGuardando(true);
    try {
      await guardarMetasManuales(
        {
          kcal: metaKcal === null ? null : Math.round(metaKcal),
          proteinaG: valor(proteina),
          carbohidratosG: valor(carbohidratos),
          grasaG: valor(grasa),
          ayunoHoras: valor(ayuno),
        },
        hoy,
      );
      setListo(true);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Metas sin plan."
        descripcion="Se usan los días que no cubre un plan. Lo que dejes vacío queda sin meta."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />
      {hayPlanHoy && (
        <Banner tono="info" titulo="Hoy manda tu plan">
          Estas metas aplican solo a los días sin plan.
        </Banner>
      )}
      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <NumberField etiqueta="kcal al día" valor={kcal} onCambio={setKcal} decimal={false} />
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-[13px] text-texto-suave">Gramos al día</legend>
          <div className="grid grid-cols-3 gap-3">
            <NumberField etiqueta="Proteína" valor={proteina} onCambio={setProteina} />
            <NumberField etiqueta="Carbohidratos" valor={carbohidratos} onCambio={setCarbohidratos} />
            <NumberField etiqueta="Grasa" valor={grasa} onCambio={setGrasa} />
          </div>
        </fieldset>
        <NumberField etiqueta="Ayuno (horas)" valor={ayuno} onCambio={setAyuno} />
        <p className="text-[12px] leading-relaxed text-texto-suave">
          Los cambios aplican desde hoy. Los días anteriores conservan las metas que tenían, y tus
          comidas no cambian.
        </p>
        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}
        {listo && <Banner tono="exito" titulo="Metas guardadas desde hoy" />}
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar metas'}
        </Button>
      </form>

      {(ajustes.metasManualesAnteriores ?? []).length > 0 && (
        <Seccion titulo="Metas anteriores" descripcion="Los días de cada periodo se siguen comparando con estas.">
          <ul className="divide-y divide-borde">
            {[...(ajustes.metasManualesAnteriores ?? [])].reverse().map((p) => (
              <li key={`${p.desde ?? 'inicio'}-${p.hasta}`} className="py-3">
                <p className="text-[13px] text-texto">{textoPeriodo(p)}</p>
                <p className="text-[12px] text-texto-suave">
                  {p.desde === null ? 'Hasta' : `Del ${formatearCortoConAnio(p.desde)} al`}{' '}
                  {formatearCortoConAnio(p.hasta)}
                </p>
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </div>
  );
}
